import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { MapPin, ChevronRight } from 'lucide-react'
import { getDirectoryCountries } from '../../lib/api'
import type { DirectoryCountry } from '../../types'
import { PageWrapper } from '../../components/layout/PageWrapper'
import { Seo } from '../../components/Seo'
import { directoryCanonicalPath } from '../../lib/directoryCanonical'
import { NavCardGridSkeleton } from '../../components/ui/Skeletons'
import { DirectorySidebar } from '../../components/directory/DirectorySidebar'
import { DirectorySearchBar } from '../../components/directory/DirectorySearchBar'
import { useDirectoryMode } from '../../hooks/useDirectoryMode'
import { DIRECTORY_MAIN_GRID, DIRECTORY_PAGE_MAX, DIRECTORY_SIDEBAR_COL } from './directoryUtils'

const FAQS = [
  { q: 'How quickly can I get an emergency plumber?', a: 'Most emergency plumbers on FixDelivered respond within 30–60 minutes in urban areas. Use our emergency service for fastest dispatch.' },
  { q: 'Are the plumbers vetted?', a: 'Plumber profiles are built from third-party industry data and public registration records. We cross-reference certification and qualification information, and only show plumbers with a minimum 4★ rating. Many carry public liability insurance, though this is not universally verified for every listing.' },
  { q: 'What types of work can I book?', a: 'Emergency callouts, boiler repair and servicing, central heating, gas safety certificates, burst pipes, blocked drains, bathroom installations and more.' },
]

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
}

function CountryCard({ country, basePath }: { country: DirectoryCountry; basePath: string }) {
  return (
    <Link
      to={`${basePath}/${country.countrySlug}`}
      className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all p-5 flex items-center justify-between"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-trust-50 flex items-center justify-center flex-shrink-0">
          <MapPin className="h-4 w-4 text-trust-500" />
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 truncate">{country.country}</p>
          <p className="text-xs text-gray-400 mt-0.5">{country.count} plumber{country.count !== 1 ? 's' : ''} listed</p>
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-trust-400 transition-colors flex-shrink-0" />
    </Link>
  )
}

export function DirectoryHub() {
  const { isEmergency, isGasSafe, isWaterSafe, basePath } = useDirectoryMode()

  const { data: countries, isLoading } = useQuery({
    queryKey: ['directory', 'countries', isEmergency, isGasSafe, isWaterSafe],
    queryFn: () => getDirectoryCountries(isEmergency, isGasSafe, isWaterSafe),
  })

  const totalUkPlumbers = countries?.reduce((sum, c) => sum + c.count, 0)

  return (
    <PageWrapper>
      <Seo
        title={`Find ${isEmergency ? 'an Emergency Plumber' : isGasSafe ? 'a Gas Safe Plumber' : isWaterSafe ? 'a WaterSafe Plumber' : 'a Plumber'} Near You | FixDelivered`}
        description={isEmergency
          ? 'Browse 24/7 emergency plumbers across the UK. All available for urgent callouts - search by country, county, and postcode.'
          : isGasSafe
            ? 'Browse Gas Safe registered plumbers across the UK. Search by country, county, and postcode for certified gas and boiler work.'
            : isWaterSafe
              ? 'Browse WaterSafe approved plumbers across the UK. Search by country, county, and postcode for certified water fittings and plumbing work.'
            : 'Find a plumber anywhere in the UK. Compare local ratings, reviews & call-out fees, or tell us your emergency and FixDelivered confirms an available plumber — usually within the hour.'}
        path={directoryCanonicalPath()}
        breadcrumbs={[{ name: 'UK', url: basePath }]}
        structuredData={faqSchema}
      />

      <div className={DIRECTORY_PAGE_MAX}>
        <div className={DIRECTORY_MAIN_GRID}>
          <div className="min-w-0">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Find {isEmergency ? 'an Emergency Plumber' : isGasSafe ? 'a Gas Safe Plumber' : isWaterSafe ? 'a WaterSafe Plumber' : 'a Plumber'}</h1>
              <p className="text-gray-500 mt-1 text-sm mb-4">
                {isEmergency
                  ? 'Browse plumbers available 24/7 for urgent callouts across the UK.'
                  : isGasSafe
                    ? 'Browse Gas Safe registered plumbers across the UK.'
                    : isWaterSafe
                      ? 'Browse WaterSafe approved plumbers across the UK.'
                    : 'Browse verified local plumbers across the UK.'}{' '}
                Search by postcode or browse by country below.
              </p>
              <DirectorySearchBar />
            </div>

            {isLoading ? (
              <NavCardGridSkeleton count={4} />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(countries ?? []).map(c => <CountryCard key={c.countrySlug} country={c} basePath={basePath} />)}
              </div>
            )}
            {!isLoading && !countries?.length && (
              <div className="text-center py-16 text-gray-400">
                <MapPin className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>No countries available yet - check back soon.</p>
              </div>
            )}
          </div>

          <div className={DIRECTORY_SIDEBAR_COL}>
            <DirectorySidebar
              count={isLoading ? undefined : totalUkPlumbers}
              areaLabel="UK"
            />
          </div>
        </div>
      </div>
    </PageWrapper>
  )
}
